type ErrorLike = {
    message?: unknown;
    userMessage?: unknown;
    code?: unknown;
    response?: {
        status?: number;
        data?: {
            detail?: unknown;
            message?: unknown;
            error?: unknown;
        };
    };
};

function extractDetail(detail: unknown): string {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => {
                if (typeof item === 'string') return item;
                if (item && typeof item === 'object' && 'msg' in item) {
                    return String((item as { msg?: unknown }).msg || '');
                }
                return '';
            })
            .filter(Boolean)
            .join(' ');
    }
    if (detail && typeof detail === 'object') {
        const record = detail as Record<string, unknown>;
        if (typeof record.message === 'string') return record.message;
        if (typeof record.error === 'string') return record.error;
    }
    return '';
}

export function isTechnicalErrorMessage(message: string): boolean {
    const text = message.trim();
    const lower = text.toLowerCase();
    if (!text) return false;
    if (text.length > 220) return true;
    if (/^(traceback|file\s+"|exception:|error processing|request failed with status code)/i.test(text)) return true;
    if (/[{}[\]"`]/.test(text) && /(detail|traceback|exception|error|stack|class|module|line)/i.test(text)) return true;
    return [
        'unexpected keyword argument',
        'objectid',
        'pydantic',
        'bson',
        'module named',
        'typeerror',
        'valueerror',
        'attributeerror',
        'keyerror',
        'syntaxerror',
        'failed to fetch dynamically imported module',
        '<html',
        'doctype html',
    ].some((needle) => lower.includes(needle));
}

function statusFallback(status?: number): string | null {
    if (!status) return null;
    if (status === 400) return 'We could not complete that request. Check the details and try again.';
    if (status === 401) return 'Your session has expired. Please sign in again.';
    if (status === 403) return 'You do not have permission to do that.';
    if (status === 404) return 'We could not find that item. Refresh the page and try again.';
    if (status === 408 || status === 504) return 'This took too long to finish. Try again in a moment.';
    if (status === 409) return 'This item was updated elsewhere. Refresh the page and try again.';
    if (status === 413) return 'That file is too large. Try a smaller file.';
    if (status === 422) return 'Some required information is missing or invalid. Review it and try again.';
    if (status >= 500) return 'Something went wrong on our side. Please try again in a moment.';
    return null;
}

export function getUserFriendlyErrorMessage(
    error: unknown,
    fallback = 'Something went wrong. Please try again.',
): string {
    const err = (error || {}) as ErrorLike;

    if (typeof err.userMessage === 'string' && err.userMessage.trim()) {
        return err.userMessage.trim();
    }

    const responseDetail = extractDetail(err.response?.data?.detail);
    const responseMessage = extractDetail(err.response?.data?.message);
    const responseError = extractDetail(err.response?.data?.error);
    const errorMessage = typeof err.message === 'string' ? err.message : '';
    const raw = (responseDetail || responseMessage || responseError || errorMessage || '').trim();
    const lower = raw.toLowerCase();

    if (
        !err.response &&
        (lower === 'network error' || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED')
    ) {
        return 'We could not reach the server. Check your connection and try again.';
    }

    if (lower.includes('email not verified')) return 'Please verify your email before signing in.';
    if (lower.includes('phone number not verified')) return 'Please verify your phone number before signing in.';
    if (lower.includes('conversation not found')) return 'This chat session expired. Start a new message and try again.';
    if (lower.includes('file already exists')) return 'File already exists. Try another one.';
    if (lower.includes('invoice not ready') || lower.includes('bill not ready')) {
        return 'This document is still processing. Try again in a moment.';
    }
    if (lower.includes('invalid bill id format')) {
        return 'We could not open that bill. Refresh the page and try again.';
    }

    if (raw && !isTechnicalErrorMessage(raw)) {
        return raw;
    }

    return statusFallback(err.response?.status) || fallback;
}
